/**
 * Voltage reference convention (binding; mirrors backend VoltageConstants.cs, 2026-09-16):
 *
 * - Un = 230 V (DIN EN 50160 / IEC 60038) is the reference of every deviation, band
 *   limit and "±10 %" shown to the user. The band limits are the norm's numbers:
 *   207–253 V (95 % rule), 195,5–253 V (100 % rule) — see utils/voltageConstants.ts.
 * - Per-unit values keep the load-flow base 400 V/√3 = 230,94 V (1,0 pu is the solver's
 *   busbar base, NOT Un). Every pu from the API means V / 230,94.
 * - Therefore: convert pu ↔ V only with puToVolts / voltsToPu, and measure "how far from
 *   nominal" only with puDeviation* / voltsDeviation* — never as (pu − 1).
 */

/** 1,0 pu of the load flow in volts (L-N): 400 V/√3 ≈ 230,94 V. Conversion base only. */
export const PU_BASE_LN_V = 400 / Math.sqrt(3);

/** Nominal L-N voltage Un = 230 V (EN 50160). The reference of every deviation and limit. */
export const NOMINAL_VOLTAGE_LN_V = 230;

/** Un expressed in load-flow pu (230 / 230,94 ≈ 0,9959). */
export const NOMINAL_PU = NOMINAL_VOLTAGE_LN_V / PU_BASE_LN_V;

/** Load-flow pu → volts (L-N). */
export function puToVolts(pu: number): number {
  return pu * PU_BASE_LN_V;
}

/** Volts (L-N) → load-flow pu. */
export function voltsToPu(volts: number): number {
  return volts / PU_BASE_LN_V;
}

/** Signed deviation from Un as a fraction (+0.05 = 5 % above 230 V). */
export function voltsDeviation(volts: number): number {
  return (volts - NOMINAL_VOLTAGE_LN_V) / NOMINAL_VOLTAGE_LN_V;
}

/** Signed deviation of a load-flow pu voltage from Un as a fraction. */
export function puDeviation(pu: number): number {
  return voltsDeviation(puToVolts(pu));
}

/** Deviation of a load-flow pu voltage from Un in percent. */
export function puDeviationPct(pu: number): number {
  return puDeviation(pu) * 100;
}

/** Deviation of a voltage in volts from Un in percent. */
export function voltsDeviationPct(volts: number): number {
  return voltsDeviation(volts) * 100;
}
